import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { ScrollText, CalendarCheck, TrendingUp, UserCog, Search, MapPin, ChevronRight } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import Modal from '../../components/admin/Modal';
import Paginacao from '../../components/admin/Paginacao';
import DetalhesLogModal from '../../components/admin/DetalhesLogModal';
import { logsAuditoriaAPI } from '../../utils/adminSegurancaAPI';
import { ACAO_LABEL, ENTIDADE_META, ENTIDADE_META_PADRAO, formatarChaveDetalhe } from '../../utils/auditLabels';
import { useItensPorPagina } from '../../hooks/useItensPorPagina';

const ITENS_POR_PAGINA_DESKTOP = 6;
const ITENS_POR_PAGINA_MOBILE = 5;

// Categorias reais são enquete/pergunta/opcao/usuario/ip_bloqueado/
// enquete_legado, mas enquete/pergunta/opcao viram uma categoria só
// ("Enquetes") pro filtro — na prática são sempre editadas juntas.
const CATEGORIAS_FILTRO = [
  { valor: 'todos', label: 'Todas as categorias' },
  { valor: 'enquete', label: 'Enquetes' },
  { valor: 'usuario', label: 'Usuários' },
  { valor: 'ip_bloqueado', label: 'IPs' },
  { valor: 'enquete_legado', label: 'Enquetes legadas' }
];

const PERIODOS = [
  { valor: 'todos', label: 'Todo o período', dias: null },
  { valor: 'hoje', label: 'Hoje', dias: 1 },
  { valor: '7dias', label: 'Últimos 7 dias', dias: 7 },
  { valor: '30dias', label: 'Últimos 30 dias', dias: 30 }
];

// enquete/pergunta/opcao mapeiam pra categoria "enquete" no filtro.
const categoriaFiltro = (entidade) => (['enquete', 'pergunta', 'opcao'].includes(entidade) ? 'enquete' : entidade);

function metaEntidade(entidade) {
  return ENTIDADE_META[entidade] || ENTIDADE_META_PADRAO;
}

function detalhesResumo(detalhes) {
  if (!detalhes || typeof detalhes !== 'object') return null;
  const chaves = Object.keys(detalhes).filter((k) => detalhes[k] !== null && detalhes[k] !== undefined && detalhes[k] !== '');
  if (chaves.length === 0) return null;
  return chaves
    .slice(0, 3)
    .map((k) => {
      const valor = detalhes[k];
      let valorTexto;
      if (typeof valor === 'object') valorTexto = 'ver detalhes';
      else if (typeof valor === 'boolean') valorTexto = valor ? 'sim' : 'não';
      else valorTexto = String(valor);
      return `${formatarChaveDetalhe(k)}: ${valorTexto}`;
    })
    .join(' · ');
}

const StatCard = ({ icon: Icone, cor, titulo, valor, delay }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex items-center gap-3.5 animate-fade-in-up"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}>
      <Icone className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-gray-900 leading-none truncate">{valor}</p>
      <p className="text-xs font-medium text-gray-500 mt-1 leading-snug">{titulo}</p>
    </div>
  </div>
);

// Fora do componente pai de propósito — evita remontar todos os cards a
// cada tecla digitada na busca.
const LogCard = ({ entrada, onAbrir, delay }) => {
  const meta = metaEntidade(entrada.entidade);
  const Icone = meta.icon;
  const resumo = detalhesResumo(entrada.detalhes);

  return (
    <button
      onClick={() => onAbrir(entrada)}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300 p-5 flex flex-col gap-3 animate-fade-in-up text-left w-full"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${meta.cor}`}>
          <Icone className="w-3 h-3" /> {meta.label}
        </span>
        <span className="text-xs text-gray-400 flex-shrink-0 text-right">
          {new Date(entrada.data_criacao).toLocaleString('pt-BR')}
        </span>
      </div>

      <p className="text-sm text-gray-800 leading-snug">
        <span className="font-semibold">{entrada.admin_username}</span>{' '}
        {ACAO_LABEL[entrada.acao] || entrada.acao}
        {entrada.entidade_id ? ` #${entrada.entidade_id}` : ''}
      </p>

      {resumo && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5 truncate">{resumo}</p>}

      <div className="flex items-center justify-between gap-1 mt-auto pt-1">
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {entrada.ip_endereco || 'IP desconhecido'}
        </p>
        <span className="text-xs text-primary-600 flex items-center gap-0.5 font-medium">
          Ver detalhes <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
};

const LogsAuditoria = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState('todos');
  const [periodo, setPeriodo] = useState('todos');
  const [pagina, setPagina] = useState(1);
  const [entradaSelecionada, setEntradaSelecionada] = useState(null);
  const itensPorPagina = useItensPorPagina(ITENS_POR_PAGINA_DESKTOP, ITENS_POR_PAGINA_MOBILE);

  useEffect(() => {
    logsAuditoriaAPI.listar()
      .then(setLogs)
      .catch((error) => toast.error(error.userMessage || 'Erro ao carregar logs'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPagina(1); }, [busca, categoria, periodo, itensPorPagina]);

  const logsFiltrados = useMemo(() => {
    const diasDoPeriodo = PERIODOS.find((p) => p.valor === periodo)?.dias;
    const limiteData = diasDoPeriodo ? Date.now() - diasDoPeriodo * 24 * 60 * 60 * 1000 : null;
    const buscaLimpa = busca.trim().toLowerCase();

    return logs.filter((entrada) => {
      if (categoria !== 'todos' && categoriaFiltro(entrada.entidade) !== categoria) return false;
      if (limiteData && new Date(entrada.data_criacao).getTime() < limiteData) return false;
      if (buscaLimpa) {
        const alvo = `${entrada.admin_username} ${ACAO_LABEL[entrada.acao] || entrada.acao} ${entrada.ip_endereco || ''} ${
          entrada.detalhes ? JSON.stringify(entrada.detalhes) : ''
        }`.toLowerCase();
        if (!alvo.includes(buscaLimpa)) return false;
      }
      return true;
    });
  }, [logs, busca, categoria, periodo]);

  const totalPaginas = Math.max(1, Math.ceil(logsFiltrados.length / itensPorPagina));
  const logsPaginados = logsFiltrados.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);

  const hojeStr = new Date().toDateString();
  const acoesHoje = logs.filter((l) => new Date(l.data_criacao).toDateString() === hojeStr).length;

  const contagemPorCategoria = {};
  logs.forEach((l) => {
    const cat = categoriaFiltro(l.entidade);
    contagemPorCategoria[cat] = (contagemPorCategoria[cat] || 0) + 1;
  });
  const categoriaMaisAtivaChave = Object.keys(contagemPorCategoria).sort((a, b) => contagemPorCategoria[b] - contagemPorCategoria[a])[0];
  const categoriaMaisAtiva = categoriaMaisAtivaChave
    ? CATEGORIAS_FILTRO.find((c) => c.valor === categoriaMaisAtivaChave)?.label || categoriaMaisAtivaChave
    : '—';

  const contagemPorAdmin = {};
  logs.forEach((l) => { contagemPorAdmin[l.admin_username] = (contagemPorAdmin[l.admin_username] || 0) + 1; });
  const adminMaisAtivo = Object.keys(contagemPorAdmin).sort((a, b) => contagemPorAdmin[b] - contagemPorAdmin[a])[0] || '—';

  return (
    <AdminLayout title="Log de auditoria">
      <Toaster position="top-center" />

      <p className="text-gray-500 max-w-lg mb-6">Toda ação administrativa que altera dados fica registrada aqui — quem fez, o quê, quando e de onde.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={ScrollText} cor="bg-primary-600" titulo="Ações registradas" valor={logs.length} delay={0} />
        <StatCard icon={CalendarCheck} cor="bg-emerald-500" titulo="Ações hoje" valor={acoesHoje} delay={0.05} />
        <StatCard icon={TrendingUp} cor="bg-[#40A1E6]" titulo="Categoria mais ativa" valor={categoriaMaisAtiva} delay={0.1} />
        <StatCard icon={UserCog} cor="bg-indigo-500" titulo="Quem mais agiu" valor={adminMaisAtivo} delay={0.15} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por quem, ação, IP..."
            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all"
          />
        </div>

        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all flex-shrink-0"
        >
          {CATEGORIAS_FILTRO.map((c) => <option key={c.valor} value={c.valor}>{c.label}</option>)}
        </select>

        <select
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all flex-shrink-0"
        >
          {PERIODOS.map((p) => <option key={p.valor} value={p.valor}>{p.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner mx-auto" />
      ) : logsFiltrados.length === 0 ? (
        <p className="text-gray-500 text-center py-12">Nenhuma ação encontrada.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {logsPaginados.map((entrada, index) => (
              <LogCard key={entrada.id} entrada={entrada} onAbrir={setEntradaSelecionada} delay={Math.min(index * 0.04, 0.4)} />
            ))}
          </div>

          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            onMudarPagina={setPagina}
            total={logsFiltrados.length}
            itensPorPagina={itensPorPagina}
          />
        </>
      )}

      {entradaSelecionada && (
        <Modal titulo="Detalhes da ação" onFechar={() => setEntradaSelecionada(null)} maxWidth="max-w-lg">
          <DetalhesLogModal entrada={entradaSelecionada} onFechar={() => setEntradaSelecionada(null)} />
        </Modal>
      )}
    </AdminLayout>
  );
};

export default LogsAuditoria;
